import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { I18nProvider as LinguiProvider } from "@lingui/react";
import {
  i18n,
  type SupportedLocale,
  detectLocale,
  loadCatalog,
  persistLocale,
} from "./i18n";

// ─── Context ─────────────────────────────────────────────────────────

interface LocaleContextValue {
  /** The currently active locale. */
  locale: SupportedLocale;
  /** Switch the active locale at runtime. */
  setLocale: (locale: SupportedLocale) => Promise<void>;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

/**
 * Hook to access the current locale and the locale-switching function.
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within an I18nProvider");
  }
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────

interface I18nProviderProps {
  children: React.ReactNode;
}

/**
 * I18n context provider that wraps the Lingui I18nProvider.
 *
 * Responsibilities:
 * - Detect initial locale (localStorage -> navigator.language -> "en")
 * - Dynamically load the matching Lingui catalog
 * - Expose a `setLocale` function for runtime locale switching
 * - Persist locale selection to localStorage (and optionally user profile)
 */
export function I18nProvider({ children }: I18nProviderProps): React.ReactElement | null {
  const [locale, setLocaleState] = useState<SupportedLocale>(detectLocale());
  const [ready, setReady] = useState(false);

  // Load the initial catalog on mount.
  useEffect(() => {
    loadCatalog(locale).then(() => setReady(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The provider that OWNS the locale also owns the document's language
  // attribute (SPEC-FRONTEND-RULE-078, WCAG 2.1 SC 3.1.1). The four HTML entry
  // points carry a static `lang="hu"` for the pre-hydration document; this is
  // the only runtime writer, so a switch to another locale keeps assistive
  // technology told the truth. Guarded for non-DOM environments.
  useEffect(() => {
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    await loadCatalog(newLocale);
    persistLocale(newLocale);
    setLocaleState(newLocale);
  }, []);

  // Don't render children until the first catalog is loaded.
  if (!ready) {
    return null;
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <LinguiProvider i18n={i18n} key={locale}>{children}</LinguiProvider>
    </LocaleContext.Provider>
  );
}
